/** View models and formatting for the overall wave plan (values come from the API). */

/** A timeline week. ``id`` identifies it; ``week`` (fiscal week) is for display. */
export interface WaveWeek {
  id: string
  week: number
  event: string
  date: string
}

/** Overall metrics of one week (fractions: 0.78 = 78%). */
export interface CalculatedWaveWeek extends WaveWeek {
  historicalWave: number
  plannedWave: number
  cumulativeWave: number
  cumulativeDemandPercent: number
  receipts: number
  averageReceipts: number
  inventory: number
  averageInventory: number
  receiptsInStores: number
  salesInStores: number
  variance: number
  /** First week after a wave where the variance is below the receipt-to-sales threshold. */
  thresholdCrossed?: boolean
  /** SKU-calculated (bottoms-up) wave and cumulative share (null: frozen before they existed). */
  skuWave: number | null
  skuCumulativeWave: number | null
}

/** The overall result shown by the summary, chart and grid. */
export interface PlanningResult {
  weeks: CalculatedWaveWeek[]
  total: number
  /** SKU-calculated (bottoms-up) total (null: frozen before it existed). */
  skuTotal: number | null
  minimumFirstWave: number
  /** The first wave the checks use: SKU-calculated (frozen before 1.5.0: the plan's). */
  firstWave: number
  /** SKU-calculated share received by the first wave week (null: frozen before it existed). */
  skuFirstWave: number | null
  /** Plan-level percentage of the first wave week. */
  plannedFirstWave: number
  /** The SKUs' POG set requirements / their buy (null: no SKU has a POG set). */
  pogSetMinimum: number | null
  /** SKUs whose first wave ships less than their POG set (a warning). */
  belowPogSetCount: number
  /** Receipts, inventory and the variance follow the SKU-calculated waves. */
  skuBasis: boolean
  inSeasonDemandPercent: number
  averageStoreReceipts: number
  averageStoreSales: number
}

/** The planning reference panel. ``null`` means the value is not available. */
export interface PlanningPanel {
  season: string | null
  stores: number
  previousStores: number | null
  skus: number
  previousSkus: number | null
  pogSet: number
  pogMax: number | null
  purchaseUnits: number
  setDate: string | null
  /** Timeline position of the set week, or -1. */
  setIndex: number
  /** The set week's header date: the planner's keyed receipt date, else the set date. */
  setWeekDate: string | null
  note: string | null
}

export const NO_WAVE = 'NO WAVE'

/**
 * Percentages are fixed-point with the stored precision (4 decimals, the API's
 * ``Numeric(9,4)``): compared and summed as integer units of 0.0001%, never as
 * display-rounded floats.
 */
export const PERCENT_DECIMALS = 4
const PERCENT_SCALE = 10 ** PERCENT_DECIMALS

/** A percent (0-100) in fixed-point units of 0.0001%. */
export const percentUnits = (percent: number) =>
  Math.round(percent * PERCENT_SCALE)

/** Whether two percents are equal at the stored precision. */
export const samePercent = (a: number, b: number) =>
  percentUnits(a) === percentUnits(b)

/** A percent as the editor shows it: every stored decimal, no float noise. */
export const editablePercent = (percent: number) =>
  String(Number(percent.toFixed(PERCENT_DECIMALS)))

/**
 * Percents rounded to the stored precision with the largest-remainder method,
 * keeping their total (rounded) unchanged: 33.333333 × 3 → 33.3334 + 33.3333 +
 * 33.3333, which still adds up to 100.
 */
export function toStoredPercents(values: number[]): number[] {
  const scaled = values.map(value => value * PERCENT_SCALE)
  const units = scaled.map(value => Math.floor(value + 1e-6))
  const target = Math.round(scaled.reduce((sum, value) => sum + value, 0))
  const order = scaled
    .map((value, index) => ({ index, remainder: value - units[index] }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
  for (const { index } of order.slice(
    0,
    Math.max(0, target - units.reduce((sum, value) => sum + value, 0))
  ))
    units[index] += 1
  return units.map(value => value / PERCENT_SCALE)
}

/** One cell of a SKU row as the auto-fill reads it. */
export interface AutoFillCell {
  weekId: string
  /** The cell's current percent (0-100). */
  percent: number
  /** The cell holds a planner quantity, not a percent. */
  quantityOverride: boolean
}

/**
 * The other cells a typed percent decides on its own, as week id to new
 * percent: 100 sets every other cell to 0 (quantity overrides too); with
 * exactly two cells, the other one gets the rest when it holds a percent. Any
 * other entry decides nothing, so nothing else changes (null).
 */
export function autoFill(
  cells: AutoFillCell[],
  weekId: string,
  percent: number
): Map<string, number> | null {
  const others = cells.filter(cell => cell.weekId !== weekId)
  if (!others.length || others.length === cells.length) return null
  if (percentUnits(percent) === percentUnits(100))
    return new Map(others.map(cell => [cell.weekId, 0]))
  if (cells.length === 2 && !others[0].quantityOverride)
    return new Map([
      [
        others[0].weekId,
        (percentUnits(100) - percentUnits(percent)) / PERCENT_SCALE,
      ],
    ])
  return null
}

/** What an auto-fill does, for the editor: "WK26 becomes 30%" / "Other waves become 0%". */
export function describeAutoFill(
  fill: Map<string, number> | null,
  labels: Record<string, string>
): string | null {
  if (!fill?.size) return null
  const [[weekId, percent]] = [...fill]
  if (fill.size === 1)
    return `${labels[weekId] ?? 'The other wave'} becomes ${editablePercent(percent)}%`
  return 'Other waves become 0%'
}

export const WAVE_PERCENT_HINT = 'Enter 0–100 with up to 4 decimal places.'

export function validateWavePercent(value: string): string | null {
  const input = value.trim()
  if (!input) return null
  if (!/^(?:\d+(?:\.\d{0,4})?|\.\d{1,4})$/.test(input) || Number(input) > 100) {
    return WAVE_PERCENT_HINT
  }
  return null
}

export const formatPercent = (value: number, decimals = 2) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: decimals,
  }).format(Math.abs(value * 100) < 0.5 * 10 ** -decimals ? 0 : value)

export const formatUnits = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.abs(value) < 0.5 ? 0 : value
  )

/** An ISO yyyy-mm-dd date at UTC midnight, or null when missing or invalid. */
function utcDate(date: string | null | undefined) {
  const value = date ? new Date(`${date}T00:00:00Z`) : null
  return value && !Number.isNaN(value.getTime()) ? value : null
}

const shortDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})
const fullDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

/** "Jun 5" (shown in UTC), or an em dash for no date. */
export const formatWaveDate = (date: string | null | undefined) => {
  const value = utcDate(date)
  return value ? shortDate.format(value) : '—'
}

/** "Jun 5, 2026", or an em dash for no date. */
export const formatFullDate = (date: string | null | undefined) => {
  const value = utcDate(date)
  return value ? fullDate.format(value) : '—'
}

export const weekLabel = (week: number) => `WK${String(week).padStart(2, '0')}`

/**
 * The week's event, or null when it is only the week's label (a week without an
 * event code): views showing both then show the label once.
 */
export const distinctEvent = (week: { week: number; event: string }) =>
  week.event === weekLabel(week.week) ? null : week.event
